import * as z from 'zod';

import { inventoryCategoryQueryLinkerSchema } from '@/_pages/settings/inventory-categories/shared/schemas';
import { inventoryVolumeQueryLinkerSchema } from '@/_pages/settings/inventory-volumes/shared/schemas';
import { fileQuerySchema } from '@/_schemas/file';
import { booleanFormSchema, currencyInput, numericStringFormSchema } from '@/_schemas/generic';
import { statusFormSchema } from '@/_schemas/status-input';

import { inventoryQuerySchema } from './inventory-query-schema';

import { vendorDetailsFormSchema } from '../vendor-details';

export const inventoryRawFormSchema = inventoryQuerySchema
  .omit({ category_id: true, inventory_volume_id: true })
  .extend({
    name: z.string().min(3),
    status: statusFormSchema,

    description: z.string(),
    manufacturer: z.string(),
    manufacturer_id: z.string(),
    images: z.array(fileQuerySchema),

    inventory_category: inventoryCategoryQueryLinkerSchema.refine((v) => v.id !== -1, {
      message: 'Inventory category is required',
    }),

    inventory_volume: inventoryVolumeQueryLinkerSchema
      .refine((v) => v.id !== -1, {
        message: 'Inventory volume is required',
      })
      .nullish(),

    last_order_cost: currencyInput.formSchema().refine((v) => testOptionalGreaterThanZero(v), {
      message: 'Please enter a positive number',
    }),

    weight: numericStringFormSchema().refine((v) => testOptionalGreaterThanZero(v), {
      message: 'Please enter a positive number',
    }),

    current_stock: numericStringFormSchema().refine((v) => testOptionalGreaterThanZero(v), {
      message: 'Please enter a positive number',
    }),

    committed: numericStringFormSchema(),

    minimum_stock: numericStringFormSchema().refine((v) => testOptionalGreaterThanZero(v), {
      message: 'Please enter a positive number',
    }),

    maximum_stock: numericStringFormSchema().refine((v) => testOptionalGreaterThanZero(v), {
      message: 'Please enter a positive number',
    }),

    units_per_order: numericStringFormSchema(),
    minimum_order: numericStringFormSchema(),

    track_stock_quantities: booleanFormSchema(),
    track_serial_numbers: booleanFormSchema(),
    discontinued: booleanFormSchema(),
    vendor_details: z.array(vendorDetailsFormSchema),
  });

export const inventoryFormSchema = inventoryRawFormSchema
  .refine(
    (values) => {
      const { vendors, minimum_order } = values;
      const primaryVendor = vendors?.find((v) => v?.pivot?.type === 'primary');
      if (primaryVendor && primaryVendor.minimum_order_required) {
        return parseInt(minimum_order || '0') > 0;
      }
      return true;
    },
    {
      path: ['minimum_order'],
      message: 'Please enter a value greater than 0',
    }
  )
  .refine(
    (values) => {
      const { vendors, minimum_order } = values;
      const primaryVendor = vendors?.find((v) => v?.pivot?.type === 'primary');
      if (primaryVendor && primaryVendor.minimum_order_required) {
        return parseInt(minimum_order || '0') > 0;
      }
      return true;
    },
    {
      path: ['vendors'],
      message:
        'To set this as your primary vendor, ensure the minimum order in the Purchasing section of the item is greater than 0, as this vendor requires a minimum order.',
    }
  );

export type InventoryFormSchema = z.infer<typeof inventoryFormSchema>;

const testOptionalGreaterThanZero = (value: string) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return true;
  }

  // for prefix/suffix
  const nValue = value.replace(/[^0-9.-]/g, '');
  if (nValue === '') {
    return true;
  }

  return parseFloat(nValue) > -1;
};

export const greaterThanZeroNumberSchema = () =>
  numericStringFormSchema().refine((v) => testOptionalGreaterThanZero(v), {
    message: 'Please enter a positive number',
  });
